import { Box, ButtonProps, css, Button as MuiButton, styled } from "@mui/material";
import { MouseEvent } from "react";

import { IconChevronDown } from "@/modules/irohana-components/assets/icons/IconChevronDown";
import { background, borderColor, button, text } from "@/modules/irohana-components/theme/colors-new";
import { typography } from "@/modules/irohana-components/theme/typography";
import { rounded } from "@/modules/irohana-components/theme/variables";
import { FCC } from "@/modules/irohana-components/types/common";

export type TSplitButtonNewVariant = "primary" | "secondary" | "tertiary";

export type TSplitButtonNewProps = {
  variant?: TSplitButtonNewVariant;
  disabled?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onClickAction?: (e: MouseEvent<HTMLButtonElement>) => void;
  onClickDropdown?: (e: MouseEvent<HTMLButtonElement>) => void;
} & Omit<ButtonProps, "variant" | "onClick">;

export const SplitButtonNew: FCC<TSplitButtonNewProps> = ({
  variant = "primary",
  disabled,
  children,
  onClick,
  onClickAction,
  onClickDropdown,
  ...rest
}) => {
  return (
    <Wrapper btnVariant={variant} className="wrapper" disabled={disabled} onClick={onClick}>
      <ActionButton btnVariant={variant} disabled={disabled} onClick={onClickAction} {...rest}>
        {children}
      </ActionButton>
      <Divider className="split-divider" btnVariant={variant} aria-hidden />
      <DropdownButton btnVariant={variant} disabled={disabled} onClick={onClickDropdown} aria-label="dropdown">
        <IconChevronDown />
      </DropdownButton>
    </Wrapper>
  );
};

const options = { shouldForwardProp: (propName: string) => propName !== "btnVariant" };

const getVariantStyles = (variant: TSplitButtonNewVariant) => {
  switch (variant) {
    case "primary":
      return css`
        background: ${button.primary};
        border-color: ${button.primary};
        color: ${text.white};
      `;
    case "secondary":
      return css`
        background: ${background.white};
        border-color: ${button.primary};
        color: ${button.primary};
      `;
    case "tertiary":
      return css`
        background: ${background.white};
        border-color: ${borderColor.higher};
        color: ${text.secondary};
      `;
  }
};

const getDisabledStyles = () => css`
  &.Mui-disabled {
    background: ${button.disable};
    border-color: ${button.disable};
    color: ${text.tertiary};
  }
`;

// Outer wrapper — hover/active here affects all buttons inside
const Wrapper = styled(Box, options)<{ btnVariant: TSplitButtonNewVariant; disabled?: boolean }>`
  display: inline-flex;
  border-radius: ${rounded.sm};
  overflow: visible;
  position: relative;

  ${({ btnVariant, disabled }) => {
    switch (btnVariant) {
      case "primary":
        return css`
          &:hover .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${button.hover};
            color: ${button.primary};
          }
          &:hover .split-divider {
            width: ${disabled ? "1px" : "2px"};
            background-color: ${disabled ? "white" : button.primary};
          }
          &:active .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${button.pressed};
          }
        `;
      case "secondary":
        return css`
          &:hover .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${button.hover};
          }
          &:active .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${button.pressed};
            color: ${text.white};
          }
        `;
      case "tertiary":
        return css`
          &:hover .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${background.base};
          }
          &:active .MuiButtonBase-root:not(.Mui-disabled) {
            background: ${button.disable};
          }
        `;
    }
  }}
`;

// Shared base for both action and dropdown buttons
const baseButtonCSS = css`
  line-height: 1;
  text-transform: none;
  min-width: unset;
  cursor: pointer;
  border: 2px solid;
  height: 40px;
  ${typography.sub16Semi}
`;

const ActionButton = styled(MuiButton, options)<{ btnVariant: TSplitButtonNewVariant }>`
  &.MuiButtonBase-root {
    ${baseButtonCSS}
    padding: 0 22px;
    border-radius: ${rounded.sm} 0 0 ${rounded.sm};
    border-right: none;
    position: relative;
    &::after {
      content: "";
      position: absolute;
      top: -5px;
      left: -5px;
      right: -5px;
      bottom: -5px;
      border: 2px solid transparent;
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      pointer-events: none;
      z-index: 10;
    }
    &:focus-visible::after,
    &.Mui-focusVisible::after {
      border-color: ${borderColor.orange};
    }
    &:focus-visible,
    &.Mui-focusVisible {
      .MuiTouchRipple-root {
        display: none;
      }
    }
    ${({ btnVariant }) => getVariantStyles(btnVariant)}
    ${getDisabledStyles}
  }
`;

const DropdownButton = styled(MuiButton, options)<{ btnVariant: TSplitButtonNewVariant }>`
  &.MuiButtonBase-root {
    ${baseButtonCSS}
    padding: 0 5px;
    border-radius: 0 ${rounded.sm} ${rounded.sm} 0;
    border-left: none;
    &::after {
      content: "";
      position: absolute;
      top: -5px;
      left: -5px;
      right: -5px;
      bottom: -5px;
      border: 2px solid transparent;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      pointer-events: none;
    }
    &:focus-visible::after,
    &.Mui-focusVisible::after {
      border-color: ${borderColor.orange};
    }
    &:focus-visible,
    &.Mui-focusVisible {
      .MuiTouchRipple-root {
        display: none;
      }
    }
    ${({ btnVariant }) => getVariantStyles(btnVariant)}
    ${getDisabledStyles}
    &:hover {
      background: inherit;
    }
  }
`;

// Vertical divider between action and dropdown
const Divider = styled(Box, options)<{ btnVariant: TSplitButtonNewVariant }>`
  align-self: stretch;
  ${({ btnVariant }) => {
    switch (btnVariant) {
      case "primary":
        return css`
          width: 1px;
          background: ${text.white};
        `;
      case "secondary":
        return css`
          width: 2px;
          background: ${button.primary};
        `;
      case "tertiary":
        return css`
          width: 2px;
          background: ${borderColor.higher};
        `;
    }
  }}

  .Mui-disabled & {
    background: ${text.tertiary};
  }
`;
